import { useState } from "react";
import { SEOHead } from "@/components/SEOHead";
import { Header } from "@/components/layout/Header";
import { Footer } from "@/components/layout/Footer";
import { SEOContentBlock } from "@/components/seo/SEOContentBlock";
import { seoCopy } from "@/content/seoCopy";
import { Button } from "@/components/ui/button";
import { Phone, Mail, MapPin, Clock, Navigation } from "lucide-react";
import { BookingPopupModal } from "@/components/home/BookingPopupModal";

const Contact = () => {
  const [bookingOpen, setBookingOpen] = useState(false);
  
  return (
    <div className="min-h-screen">
      <SEOHead
        title="Contact Us | Son's Guadalupe — New Braunfels, TX"
        description="Contact Son's Guadalupe at 877-577-7667 or info@sonsgetaways.com. Visit us at 7308 River Road, New Braunfels. Open 9am–7pm daily."
        canonical="/contact"
      />
      <BookingPopupModal open={bookingOpen} onOpenChange={setBookingOpen} />
      <Header />
      <main className="pt-32 md:pt-36">
        <section className="section-padding bg-background">
          <div className="container-narrow mx-auto">
            <div className="text-center mb-12">
              <h1 className="font-lora text-4xl md:text-5xl font-bold text-teal mb-4">
                Contact Us
              </h1>
              <p className="text-muted-foreground max-w-2xl mx-auto">
                Have questions? We'd love to hear from you. Reach out and we'll respond as soon as we can.
              </p>
            </div>
            
            <div className="grid md:grid-cols-2 gap-8">
              {/* Contact Info */}
              <div className="bg-card rounded-2xl p-8 shadow-card">
                <h2 className="font-heading text-2xl font-bold text-foreground mb-6">
                  Get in Touch
                </h2>
                
                <div className="space-y-6">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 rounded-xl bg-teal/10 flex items-center justify-center flex-shrink-0">
                      <Phone className="w-6 h-6 text-teal" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-foreground mb-1">Phone</h3>
                      <a href="tel:877-577-7667" className="text-teal hover:underline text-lg font-medium">
                        877-577-SONS (7667)
                      </a>
                    </div>
                  </div>
                  
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 rounded-xl bg-teal/10 flex items-center justify-center flex-shrink-0">
                      <Mail className="w-6 h-6 text-teal" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-foreground mb-1">Email</h3>
                      <a href="mailto:info@sonsgetaways.com" className="text-teal hover:underline">
                        info@sonsgetaways.com
                      </a>
                    </div>
                  </div>
                  
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 rounded-xl bg-teal/10 flex items-center justify-center flex-shrink-0">
                      <MapPin className="w-6 h-6 text-teal" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-foreground mb-1">Location</h3>
                      <p className="text-muted-foreground">
                        7308 River Road<br />
                        New Braunfels, TX 78132
                      </p>
                    </div>
                  </div>
                  
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 rounded-xl bg-teal/10 flex items-center justify-center flex-shrink-0">
                      <Clock className="w-6 h-6 text-teal" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-foreground mb-1">Hours</h3>
                      <p className="text-muted-foreground">
                        Open Daily: 10am - 7pm<br />
                        (Seasonal hours may vary)
                      </p>
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Quick Actions */}
              <div className="bg-deep-green rounded-2xl p-8 text-white">
                <h2 className="font-heading text-2xl font-bold mb-6">
                  Ready to Book?
                </h2>
                <p className="text-white/90 mb-8 leading-relaxed">
                  Skip the wait and reserve your private cabana on the Guadalupe River today. 
                  Our team is standing by to help you plan the perfect river getaway.
                </p>
                
                <div className="space-y-4">
                  <Button variant="warm" size="lg" className="w-full" onClick={() => setBookingOpen(true)}>
                    Reserve Your Cabana
                  </Button>
                  <Button variant="hero-outline" size="lg" className="w-full">
                    <Phone className="w-4 h-4 mr-2" />
                    Call 877-577-SONS
                  </Button>
                </div>
                
                <div className="mt-8 pt-6 border-t border-white/20">
                  <p className="text-white/80 text-sm">
                    🌊 Don't forget to use promo code <span className="font-bold text-warm-sand">SONS25</span> for 25% off your 2026 booking!
                  </p>
                </div>
              </div>
            </div>
            
            {/* Map Section */}
            <div className="mt-8 bg-card rounded-2xl overflow-hidden shadow-card">
              <div className="p-6 border-b border-border">
                <div className="flex items-center justify-between flex-wrap gap-4">
                  <div>
                    <h2 className="font-heading text-2xl font-bold text-foreground">
                      Find Us
                    </h2>
                    <p className="text-muted-foreground mt-1">
                      7308 River Road, New Braunfels, TX 78132
                    </p>
                  </div>
                  <Button 
                    variant="default"
                    size="lg"
                    onClick={() => window.open('https://www.google.com/maps/dir/?api=1&destination=7308+River+Road,+New+Braunfels,+TX+78132', '_blank')}
                  >
                    <Navigation className="w-4 h-4 mr-2" />
                    Get Directions
                  </Button>
                </div>
              </div>
              <div className="aspect-[16/9] md:aspect-[21/9]">
                <iframe
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3467.8!2d-98.1456!3d29.7199!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x865cbd1b8b8b8b8b%3A0x8b8b8b8b8b8b8b8b!2s7308+River+Rd%2C+New+Braunfels%2C+TX+78132!5e0!3m2!1sen!2sus!4v1620000000000!5m2!1sen!2sus"
                  width="100%"
                  height="100%"
                  style={{ border: 0 }}
                  allowFullScreen
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  title="Son's Guadalupe Location"
                  className="w-full h-full"
                />
              </div>
            </div>
          </div>
        </section>
      </main>
      <SEOContentBlock {...seoCopy.contact} />
      <Footer />
    </div>
  );
};

export default Contact;